import { inject, Injectable } from '@angular/core';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { catchError, defaultIfEmpty, filter, first, map, switchMap, tap } from 'rxjs/operators';
import { FraudCheckService, LOCAL_STORAGE_FRAUD_KEY_DEPOSIT } from './fraud-check.service';
import { UbidexService } from './ubidex.service';
import { CookieService, LocalstorageService, PlatformService } from 'ngx-unificator/services';
import { WS_RESPONSE_TYPE } from '../enums/ws-response-type.enum';
import { UserService } from './user/user.service';
import { WrSocketsService } from './wr-sockets.service';
import { TransactionActions, UserTransactionsService } from './user/user-transactions.service';
import { GoogleTagManagerService } from './google-tag-manager.service';

@Injectable({
  providedIn: 'root',
})
export class SocketSuccessHandlerService {
  private _fraud = inject(FraudCheckService);
  private _ubidex = inject(UbidexService);
  private _storage = inject(LocalstorageService);
  private _wrSocket = inject(WrSocketsService);
  private _platform = inject(PlatformService);
  private _user = inject(UserService);
  private _transactions = inject(UserTransactionsService);
  private _cookie = inject(CookieService);
  private _gtm = inject(GoogleTagManagerService);

  private _isPending = false;
  private _sentUbidexTransactionIds = new Set<string>();

  /**
   * Starts listening to socket success and handle actions
   */
  initHandler(): void {
    if (!this._platform.isBrowser) return;

    this._user.auth$
      .pipe(
        filter(auth => !!auth),
        first(),
        switchMap(() =>
          this._wrSocket.depositSuccessNotify$.pipe(
            filter(() => !this._isPending),
            tap(() => (this._isPending = true)),
            switchMap((data: any) => this._handleDepositSuccess(data)),
            tap(() => (this._isPending = false)),
            catchError(err => {
              this._isPending = false;
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();
  }

  /**
   * Handles deposit success notification by processing seon and ubidex requests
   */
  private _handleDepositSuccess(data: any): Observable<any> {
    const shouldRunSeon = this._shouldHandleSeonRequest(data);
    const requests = [];

    requests.push(
      this._transactions.transactionList().pipe(
        first(),
        map((list) =>
          (list ?? []).filter((e) => e.action === TransactionActions.DEPOSIT && e.success).reduce((a, b) => (!a || b.created_at > a.created_at ? b : a), null),
        ),
        filter((transaction) => !!transaction?.id && transaction?.id !== +this._cookie.get('__latestTID')),
        defaultIfEmpty(null),
        switchMap((transaction) =>
          transaction
            ? this._user.getPlayerStats().pipe(
                first(),
                tap((stats) => {
                  if (transaction?.id) {
                    this._cookie.set('__latestTID', transaction?.id, 0.1, '/');
                    const customerType = this._detectCustomerType(stats);
                    this._gtm.successDeposit(transaction, this._user.info.deposit_bonus_code, customerType);
                  }
                }),
              )
            : of(null),
        ),
      ),
    );

    if (shouldRunSeon) {
      requests.push(
        this._executeSeonRequest().pipe(
          tap(resp => {
            if (resp) {
              this._storage.clearItem(LOCAL_STORAGE_FRAUD_KEY_DEPOSIT);
            }
          }),
        ),
      );
    }

    const transactionId = String(data.transactionId);
    if (!this._sentUbidexTransactionIds.has(transactionId)) {
      requests.push(
        this._executeUbidexRequest(data).pipe(
          tap(() => this._sentUbidexTransactionIds.add(transactionId)),
          catchError((error) => {
            console.log('Ubidex request failed:', error);
            return EMPTY;
          }),
        ),
      );
    }

    if (requests.length === 0) {
      this._notifyDepositAccepted(data);
      return of(data);
    }

    return forkJoin(requests).pipe(
      tap(() => {
        this._notifyDepositAccepted(data);
      }),
      map(() => data),
      catchError(error => {
        console.log('Deposit success handling failed:', error);
        return EMPTY;
      }),
    );
  }

  /**
   * Determines if Seon fraud request should be executed
   */
  private _shouldHandleSeonRequest(data: any): boolean {
    return (
      this._fraud.isBodyFraudStorageLength(LOCAL_STORAGE_FRAUD_KEY_DEPOSIT) &&
      this._fraud.getUserIdFromDecodedString() === Number(data.playerId)
    );
  }

  private _executeSeonRequest(): Observable<any> {
    const fraudData = this._storage.get(LOCAL_STORAGE_FRAUD_KEY_DEPOSIT);

    return this._fraud.postFraudData(fraudData).pipe(
      catchError(error => {
        console.log('Fraud check request failed:', error);
        return EMPTY;
      }),
    );
  }

  private _executeUbidexRequest(data: any): Observable<any> {
    return this._ubidex.sendDepositEventFromApi(data.transactionId);
  }

  /**
   * Sends deposit accepted notification via socket
   */
  private _notifyDepositAccepted(data: any): void {
    this._wrSocket.socket.emit(WS_RESPONSE_TYPE.NOTIFY_ACCEPTED, {
      messageUid: data?.uid,
    });
  }

  private _detectCustomerType(playerStats) {
    if (playerStats) {
      const isNew = playerStats?.deposits_count <= 1;

      return isNew ? 'new' : 'returning';
    } else {
      return null;
    }
  }
}
